import { Dispatch, SetStateAction } from "react";
import { GetCollaboratorsForUserOfTracksResponse, ProjectFile } from "../../proto/api_pb";
import { GetMyTracksResponse, TrackState } from "../../proto/api_pb";
import { GetArtistsForUserOfTracksResponse } from "../../proto/api_pb";
import { fetchTracks, postTrack } from "../../services/trackService";
import { TrackFilterParams, TrackObj, VersionAction } from "../MyTracks/types";
import { getArtistsForUser } from "../../services/userService";
import { getCollaboratorsForUser } from "../../services/userService";
import { convertToMyTrackObj } from "../MyTracks/utils";
import { RpcError } from "grpc-web";
import { toast } from "react-toastify";

export const fetchArtistsForUser: (
  token: string,
  userId: string,
  setAllArtistList: Dispatch<SetStateAction<string[]>>
) => Promise<void> = async (
  token: string,
  userId: string,
  setAllArtistList: Dispatch<SetStateAction<string[]>>
) => {
    const artistsResponse = await getArtistsForUser(userId, token);
    if (artistsResponse instanceof GetArtistsForUserOfTracksResponse) {
      const uniqueArray = artistsResponse
        .getArtistsList()
        .filter(
          (value, index) =>
            artistsResponse.getArtistsList().indexOf(value) === index
        );
      setAllArtistList(uniqueArray);
    } else {
      console.log(artistsResponse);
    }
  };

export const fetchCollaboratorsForUser: (
  token: string,
  userId: string,
  setCollaboratorsOptionsList: Dispatch<SetStateAction<string[]>>
) => Promise<void> = async (
  token: string,
  userId: string,
  setCollaboratorsOptionsList: Dispatch<SetStateAction<string[]>>
) => {
    const res = await getCollaboratorsForUser(userId, token);
    if (res instanceof GetCollaboratorsForUserOfTracksResponse) {
      const cbArray: string[] = [];
      res.getCollaboratorsList().forEach((c) => {
        cbArray.push(c.getUsername());
      });
      setCollaboratorsOptionsList(
        cbArray.filter((value, index) => cbArray.indexOf(value) === index)
      );
    }
  };

export const getTracks: (
  userId: string,
  filterAndSortParams: TrackFilterParams,
  token: string,
  setTracks: Dispatch<SetStateAction<TrackObj[]>>,
  page?: number,
  setIsScrollEnabled?: Dispatch<SetStateAction<boolean>>,
  queryValue?: string,
  setIsLoading?: Dispatch<SetStateAction<boolean>>,
  projectId?: string,
  enterpriseId?: string,
  role?: number,
  isCompletedTracks?: boolean,
  folderId?: string | undefined
) => Promise<void> = async (
  userId: string,
  filterAndSortParams: TrackFilterParams,
  token: string,
  setTracks: Dispatch<SetStateAction<TrackObj[]>>,
  page?: number,
  setIsScrollEnabled?: Dispatch<SetStateAction<boolean>>,
  queryValue?: string,
  setIsLoading?: Dispatch<SetStateAction<boolean>>,
  projectId?: string,
  enterpriseId?: string,
  role?: number,
  isCompletedTracks?: boolean,
  folderId?: string | undefined
) => {
    const trackFilterParams: TrackFilterParams = {
      userIds: [userId],
      sortBy: filterAndSortParams?.sortBy || "",
      isAscending: filterAndSortParams.isAscending || false,
      genreList: filterAndSortParams?.genreList || [],
      artistList: filterAndSortParams?.artistList || [],
      collaboratorsList: filterAndSortParams?.collaboratorsList || [],
      TrackPrivacy: filterAndSortParams?.TrackPrivacy || "",
      page: page ? page : 0,
      limit: 50,
      bpmStartValue: filterAndSortParams?.bpmStartValue || 0,
      bpmEndValue: filterAndSortParams?.bpmEndValue || 500,
      status: filterAndSortParams?.status ?? "",
      isCompletedTracks: isCompletedTracks ?? false
    };
    try {
      const response = (await fetchTracks(
        token,
        trackFilterParams,
        queryValue ?? "",
        projectId,
        enterpriseId,
        role,
        folderId
      )) as GetMyTracksResponse;
      const responseList = response.getTracksList();
      const trackObjList = responseList.map(convertToMyTrackObj);
      setTracks((prev) => {
        const existingTrackIds = new Set(prev.map(track => track.track?.getId()));
        const newTracks = trackObjList.filter(track => !existingTrackIds.has(track.track?.getId()));
        return [...prev, ...newTracks];
      });
      if (responseList.length < 50 && setIsScrollEnabled) {
        setIsScrollEnabled(false);
      } else if (setIsScrollEnabled) {
        setIsScrollEnabled(true);
      }
    } catch (e) {
      console.log(e);
      if (setIsLoading) setIsLoading(false);
    } finally {
      if (setIsLoading) setIsLoading(false);
    }
  };

export const handlePublish: (
  checkedTracks: TrackObj[],
  token: string,
  tracks: TrackObj[],
  userId: string,
  isTracksPublic: boolean
) => Promise<void> = async (
  checkedTracks: TrackObj[],
  token: string,
  tracks: TrackObj[],
  userId: string,
  isTracksPublic: boolean
) => {
    try {
      if (checkedTracks && checkedTracks.length !== 0) {
        await Promise.all(
          checkedTracks.map(async (checkedTrack) => {
            if (isTracksPublic) {
              checkedTrack.track?.setState(TrackState.TRACK_STATE_DRAFT);
            } else {
              checkedTrack.track?.setState(TrackState.TRACK_STATE_PUBLISHED);
            }
            const postTrackResponse = await postTrack(
              checkedTrack.track!,
              token,
              userId
            );
            if (postTrackResponse instanceof RpcError) {
              throw postTrackResponse;
            }
            updateOrInsertTrackInState(
              {
                track: postTrackResponse.getTrack(),
                trackUrl: "",
                isPlaying: false,
                versionCount: VersionAction.TrackEdited,
                latestVersion: undefined,
                notifications: undefined

              },
              tracks
            );
          })
        );
        if (isTracksPublic) {
          toast.info("Tracks are private now");
        } else {
          toast.info("Tracks are public now");

        }
      }
    } catch (error) {
      console.error(error);
    }
  };

export function updateOrInsertTrackInState(
  track: TrackObj,
  prevTracks: TrackObj[]
): TrackObj[] {
  const trackId = track.track?.getId();
  const existingTrackIndex = prevTracks.findIndex(
    (track) => track.track?.getId() === trackId
  );

  if (existingTrackIndex !== -1) {
    const updatedTracks = [...prevTracks];
    const existingTrack = updatedTracks[existingTrackIndex];

    if (track.versionCount === VersionAction.VersionAdded) {
      updatedTracks[existingTrackIndex] = {
        ...existingTrack,
        versionCount: existingTrack.versionCount! + 1,
        latestVersion: track.latestVersion,
      };
    } else {
      updatedTracks[existingTrackIndex] = {
        ...existingTrack,
        track: track.track,
      };
    }
    return updatedTracks;
  } else {
    return [track, ...prevTracks];
  }
}


export const getModalMessage = (checkedTracks: TrackObj[], checkedFiles: ProjectFile[]): string => {
  const isHubPage = window.location.pathname.includes("my-tracks");
  const action = isHubPage ? "delete" : "remove";
  if (checkedTracks.length > 0) {
    return checkedTracks.length > 1
      ? `Are you sure you want to ${action} all tracks?`
      : `Are you sure you want to ${action} the track?`;
  }

  if (checkedFiles.length > 0) {
    return checkedFiles.length > 1
      ? `Are you sure you want to ${action} all files?`
      : `Are you sure you want to ${action} the file?`;
  }
  return "";
};